import React, { useState } from "react";
import { IconButton, Badge, Menu, MenuItem, Box, Typography, Divider } from "@mui/material";
import { HiOutlineBell } from "react-icons/hi";
import { useNavigate } from "react-router";
import { useGetMyUnreadCount } from "@/graphql/__generated__/getMyUnreadCount.generated";
import { useGetMyNotifications } from "@/graphql/__generated__/getMyNotifications.generated";
import { useMarkNotificationRead } from "@/graphql/__generated__/markNotificationRead.generated";
import { authStorage } from "@/utils/authStorage";
import { formatDistanceToNow } from "date-fns";

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const userData = authStorage.getUserData();
  const isNonAdmin = userData?.me?.usertype !== "ADMIN";

  const { data: unreadData, refetch: refetchCount } = useGetMyUnreadCount({
    pollInterval: 15000,
  });

  const { data: notifData } = useGetMyNotifications({
    variables: { limit: 5 },
    skip: !anchorEl,
  });

  const [markRead] = useMarkNotificationRead();

  const unreadCount = unreadData?.getMyUnreadCount || 0;
  const notifications = notifData?.getMyNotifications || [];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notif: any) => {
    if (!notif.isRead) {
      await markRead({ variables: { id: notif.id } });
      refetchCount();
    }
    handleClose();

    // Parse params if it's a string
    let params = notif.params;
    if (typeof params === "string") {
      try {
        params = JSON.parse(params);
      } catch (e) {
        console.log(3);
      }
    }
    const storageUnitId = params?.storageUnitId || notif.storageUnitId;

    // Deep linking
    if (notif.screen === "alert-detail" || (notif.entityId && notif.alertId)) {
      const prefix = isNonAdmin ? "/app/partner" : "/app/2";
      if (notif.entityType === "VEHICLE" || params?.entityType === "VEHICLE") {
        const vPrefix = isNonAdmin ? "/app/partner" : "/app/admin";
        navigate(`${vPrefix}/vehicles/${notif.entityId}?tab=alerts&alertId=${notif.alertId}`);
      } else if (storageUnitId) {
        navigate(
          `${prefix}/storageunit/${storageUnitId}/chamber/${notif.entityId}/telemetry?tab=alerts&alertId=${notif.alertId}`
        );
      } else {
        navigate(`${isNonAdmin ? "/app/partner" : "/app/0"}/alerts?alertId=${notif.alertId}`);
      }
    } else if (notif.screen === "inventory") {
      navigate(isNonAdmin ? "/app/partner/inventory" : "/app/3/inventory");
    } else {
      navigate(`${isNonAdmin ? "/app/partner" : "/app/0"}/notifications`);
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate(`${isNonAdmin ? "/app/partner" : "/app/0"}/notifications`);
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} sx={{ ml: 2 }}>
        <Badge badgeContent={unreadCount} color="error">
          <HiOutlineBell size={24} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 350, maxHeight: 400, mt: 1 },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              No recent notifications
            </Typography>
          </Box>
        ) : (
          notifications.map((n: any) => (
            <MenuItem
              key={n.id}
              onClick={() => handleNotificationClick(n)}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: "1px solid #f3f4f6",
                bgcolor: n.isRead ? "transparent" : "#f0f9ff",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" fontWeight={n.isRead ? 500 : 700} noWrap>
                    {n.title}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary" noWrap sx={{ mt: 0.5 }}>
                  {n.body}
                </Typography>
                <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
                  {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}

        <Divider />
        <MenuItem onClick={handleViewAll} sx={{ justifyContent: "center", py: 1.5 }}>
          <Typography variant="body2" color="primary" fontWeight={600}>
            View All Notifications
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default NotificationBell;
